import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { cloneDeep } from 'lodash';
import { environment } from '../../../environments/environment';
import { AppState } from '../models';

const initialState: AppState = {
  loadingData: false,
};

@Injectable()
export class StoreService {
  public readonly state$ = new BehaviorSubject<AppState>(initialState);

  constructor() {
    this.state$.subscribe((s) => {
      this.log('STATE CHANGE', s);
    });
  }

  public reset() {
    this.state$.next(initialState);
  }

  public select = <K extends keyof AppState>(key: K) =>
    this.state$.pipe(
      map((state) => state[key]),
      distinctUntilChanged(),
    );

  public select2 = <K extends keyof AppState, K2 extends keyof Required<AppState>[K]>(key: K, subkey: K2) => {
    return this.state$.pipe(
      map((state) => (state[key] as AppState[K])?.[subkey]),
      distinctUntilChanged(),
    );
  };

  public set = <K extends keyof AppState>(key: K, data: AppState[K]) => {
    this.state$.next({
      ...this.state$.value,
      [key]: cloneDeep(data), // cloneDeep to ensure a edit that was cancelled does not mutate the state
    });
  };

  public set2 = <K extends keyof AppState, K2 extends keyof Required<AppState>[K]>(
    key: K,
    subkey: K2,
    data: Required<AppState>[K][K2],
  ) => {
    const currentValue = this.state$.value[key] as Record<K2, Required<AppState>[K][K2]>;
    this.state$.next({
      ...this.state$.value,
      [key]: {
        ...currentValue,
        [subkey]: cloneDeep(data),
      },
    });
  };

  public set3 = <K extends keyof AppState>(key: K, data: AppState[K]) => {
    const currentValue = this.state$.value[key] as AppState[K];
    const newValue = cloneDeep(data);

    if (typeof currentValue === 'object' && typeof newValue === 'object') {
      this.state$.next({
        ...this.state$.value,
        [key]: {
          ...currentValue,
          ...newValue,
        },
      });
    } else if (typeof currentValue !== 'object' && typeof newValue !== 'object') {
      this.state$.next({
        ...this.state$.value,
        [key]: {
          currentValue,
          newValue,
        },
      });
    } else if (typeof currentValue !== 'object' && typeof newValue === 'object') {
      this.state$.next({
        ...this.state$.value,
        [key]: {
          currentValue,
          ...newValue,
        },
      });
    } else if (typeof currentValue === 'object' && typeof newValue !== 'object') {
      this.state$.next({
        ...this.state$.value,
        [key]: {
          ...currentValue,
          newValue,
        },
      });
    }
  };

  private log = (...args: unknown[]) => {
    if (!environment.production) {
      console.debug(...args);
    }
  };
}
